import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, flexRender, getCoreRowModel, SortingState, useReactTable } from '@tanstack/react-table';

import { Dimensions, Metrics, Sources } from 'components/insights/constants';
import {
  DataTableTheme,
  Table as StyledTable,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderFooterRow,
  TableRow,
  TableSortIcon,
} from 'components/shared/DataTable';
import ExpandableArea from 'components/shared/ExpandableArea';
import TablePaginator from 'components/shared/table/TablePaginator';
import usePagination from 'hooks/usePagination';
import { simpleDate } from 'lib/date';
import { retrieveAnalytics } from 'services/api/insights/analytics';
import { serializeSort } from 'stores/helpers';
import {
  getReferralAnalyticsFilters,
  getSlideoutEntityFilter,
  useReferralsInsightsStore,
} from 'stores/referralsInsightsStore';
import ArrowUpDownIcon from 'svg/ArrowUpDownIcon';

type ReferralRow = {
  reviewId: string;
  episodeId: string;
  patientName: string;
  referredBy: string;
  referredTo: string;
  createdAt: string;
};

const columnHelper = createColumnHelper<ReferralRow>();

const columns = [
  columnHelper.accessor('patientName', {
    id: Dimensions.PATIENT_NAME,
    header: 'Patient Name',
    cell: (info) => {
      const name = info.getValue();
      const { episodeId, reviewId } = info.row.original;
      if (!name) return '—';
      if (!episodeId || !reviewId) return name;
      // Deep link into the patient story at the specific referral.
      return (
        <ReferralLink href={`/patients/${episodeId}/referrals/${reviewId}`} target='_blank' rel='noopener noreferrer'>
          {name}
        </ReferralLink>
      );
    },
    enableSorting: true,
  }),
  columnHelper.accessor('referredBy', {
    id: Dimensions.SENDING_GROUP,
    header: 'Referred By',
    cell: (info) => info.getValue() || '—',
    enableSorting: true,
  }),
  columnHelper.accessor('referredTo', {
    id: Dimensions.RECEIVING_GROUP,
    header: 'Referred To',
    cell: (info) => info.getValue() || '—',
    enableSorting: true,
  }),
  columnHelper.accessor('createdAt', {
    id: Dimensions.DATE,
    header: 'Referral Created Date',
    cell: (info) => (info.getValue() ? simpleDate(info.getValue()) : '—'),
    enableSorting: true,
  }),
];

const QUERY_DIMENSIONS = [
  Dimensions.REVIEW_ID,
  Dimensions.EPISODE_ID,
  Dimensions.PATIENT_NAME,
  `${Dimensions.SENDING_GROUP}:with_id`,
  `${Dimensions.RECEIVING_GROUP}:with_id`,
  Dimensions.DATE,
];

// review_id:count is always 1 at this grain — the analytics endpoint requires at least one metric.
const QUERY_METRICS = [Metrics.REVIEW_ID_COUNT];

const METRIC_STATUS_FILTER: Record<string, string | undefined> = {
  denial_rate: 'rejected',
  acceptance_rate: 'accepted',
  response_time: 'pending',
};

export default function PatientListSection() {
  // ExpandableArea expects its parent to have no horizontal padding so its handle (bar with the
  // chevron + Expand/Collapse) spans edge to edge. The title gets its own padded header, and
  // ExpandableArea's InteriorContainer supplies the padding for the table when expanded.
  return (
    <Wrapper>
      <Header>
        <Title>Referrals</Title>
      </Header>
      <ExpandableArea>
        <PatientListTable />
      </ExpandableArea>
    </Wrapper>
  );
}

// Split so the query (and any other rendering work) only kicks off once the user expands the section.
// ExpandableArea unmounts children when collapsed.
function PatientListTable() {
  const baseFilters = useReferralsInsightsStore((state) => getReferralAnalyticsFilters(state));
  const slideoutContext = useReferralsInsightsStore((state) => state.slideoutContext);

  const analyticsFilters = useMemo(() => {
    if (!baseFilters) return null;
    const statusFilter = slideoutContext?.kind === 'metric' ? METRIC_STATUS_FILTER[slideoutContext.metric] : undefined;
    // For entity drills, lock results to the clicked entity (same merge the rest of the slideout uses).
    const entityFilter = slideoutContext ? getSlideoutEntityFilter(slideoutContext) : null;
    return {
      ...baseFilters,
      ...(entityFilter ?? {}),
      ...(statusFilter ? { referralStatus: statusFilter } : {}),
    };
  }, [baseFilters, slideoutContext]);

  const pagination = usePagination({ pageSize: 10 });
  const [sort, setSort] = useState<SortingState>([{ id: Dimensions.DATE, desc: true }]);

  useEffect(() => {
    pagination.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideoutContext]);

  const queryParams = useMemo(
    () => ({
      source: Sources.INSIGHTS_REFERRALS,
      dimensions: QUERY_DIMENSIONS,
      metrics: QUERY_METRICS,
      sortBy: serializeSort(sort),
      page: pagination.paging.page,
      pageSize: pagination.paging.pageSize,
    }),
    [sort, pagination.paging]
  );

  const { data, isLoading } = useQuery({
    queryKey: ['referral-slideout-patient-list', queryParams, analyticsFilters],
    queryFn: () => retrieveAnalytics(queryParams, analyticsFilters),
    enabled: !!analyticsFilters,
  });

  // `:with_id` widens each entity dim into `{name}` (the display value, aliased from the
  // attribute's name column) and `{name}_id` headers, so use the response's dimensionHeaders to
  // look up indices rather than hand-counted positions.
  const rows = useMemo<ReferralRow[]>(() => {
    const responseRows = data?.data ?? [];
    const headers = data?.dimensionHeaders ?? [];
    const idx = (name: string) => headers.findIndex((h) => h.name === name);
    const reviewIdIdx = idx(Dimensions.REVIEW_ID);
    const episodeIdIdx = idx(Dimensions.EPISODE_ID);
    const patientNameIdx = idx(Dimensions.PATIENT_NAME);
    const sendingGroupIdx = idx(Dimensions.SENDING_GROUP);
    const receivingGroupIdx = idx(Dimensions.RECEIVING_GROUP);
    const dateIdx = idx(Dimensions.DATE);
    return responseRows.map((row): ReferralRow => {
      const dimValue = (i: number) => (i >= 0 ? row.dimensionValues[i]?.value : undefined);
      return {
        reviewId: String(dimValue(reviewIdIdx) ?? ''),
        episodeId: String(dimValue(episodeIdIdx) ?? ''),
        patientName: String(dimValue(patientNameIdx) ?? ''),
        referredBy: String(dimValue(sendingGroupIdx) ?? ''),
        referredTo: String(dimValue(receivingGroupIdx) ?? ''),
        createdAt: String(dimValue(dateIdx) ?? ''),
      };
    });
  }, [data]);

  // The analytics endpoint uses Kaminari's `without_count` (aliased GROUP BY breaks the count
  // query), so the response carries no total — only a `links.next.url` when there's another page.
  // We pass no pageCount to TablePaginator; it falls back to a count-less "Page N" display.
  const hasNextPage = !!data?.links?.next?.url;
  const currentPage = pagination.paging.page;
  const showPaginator = hasNextPage || currentPage > 1;

  const table = useReactTable({
    columns,
    data: rows,
    state: { sorting: sort },
    onSortingChange: (updater) => {
      const next = updater instanceof Function ? updater(sort) : updater;
      setSort(next);
      pagination.setPageIndex(0);
    },
    manualSorting: true,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <StyledTable $theme={DataTableTheme.DEFAULT}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderFooterRow key={headerGroup.id} $theme={DataTableTheme.DEFAULT}>
              {headerGroup.headers.map((header) => (
                <TableHeaderCell
                  key={header.id}
                  isSorted={!!header.column.getIsSorted()}
                  isSortable={header.column.getCanSort()}
                  onClick={header.column.getToggleSortingHandler()}
                  width={header.getSize()}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && (
                    <TableSortIcon
                      as={ArrowUpDownIcon}
                      direction={header.column.getIsSorted() || 'none'}
                      height={10}
                      width={10}
                    />
                  )}
                </TableHeaderCell>
              ))}
            </TableHeaderFooterRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.length === 0 && !isLoading && (
            <TableRow>
              <EmptyCell colSpan={columns.length}>No referrals found for this period.</EmptyCell>
            </TableRow>
          )}
          {rows.length > 0 &&
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} width={cell.column.getSize()}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
        </TableBody>
      </StyledTable>
      {showPaginator && (
        <PaginatorRow>
          <TablePaginator
            canNextPage={hasNextPage}
            canPreviousPage={currentPage > 1}
            gotoPage={pagination.setPageIndex}
            nextPage={() => {}}
            previousPage={() => {}}
            onPageChange={pagination.setPageIndex}
            pageIndex={currentPage - 1}
          />
        </PaginatorRow>
      )}
    </>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 24px 24px 12px;
`;

const PaginatorRow = styled.div`
  padding-top: 12px;
`;

const Title = styled.h3`
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.colors.black};
`;

const ReferralLink = styled.a`
  color: ${({ theme }) => theme.colors.primaryBlue};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyCell = styled.td`
  padding: 24px;
  width: 100%;
  text-align: center;
  color: ${({ theme }) => theme.colors.black50};
`;
